import { useState } from 'react'
import { errorMessage } from '@/shared/api'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { Field, Input, Textarea } from '@/shared/ui/field'
import { usePublicBookingMutation } from '../api/queries'
import type { BookingConfirmation, PublicExcursion } from '../model/schema'

interface BookingFormProps {
  /** Идентификатор приходит из маршрута: наружу выход его не отдаёт. */
  excursionId: string
  excursion: PublicExcursion
  token: string
  onDone: (confirmation: BookingConfirmation) => void
}

/**
 * Форма записи. Согласие на обработку ПДн обязательно (152-ФЗ ст. 9) —
 * без отметки кнопка не работает, а формулировку показываем ту же, что
 * сохранит бэкенд.
 *
 * Компания идёт одной записью с числом мест, а не отдельной анкетой на каждого.
 */
export function BookingForm({ excursionId, excursion, token, onDone }: BookingFormProps) {
  const place = usePublicBookingMutation()
  const [form, setForm] = useState({ name: '', phone: '', seats: '1', note: '' })
  const [consent, setConsent] = useState(false)

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    place.mutate(
      {
        excursionId,
        token,
        name: form.name.trim(),
        phone: form.phone.trim(),
        seats: Number(form.seats) || 1,
        note: form.note.trim(),
        consentGiven: consent,
      },
      { onSuccess: onDone },
    )
  }

  const ready = consent && form.name.trim().length > 0 && form.phone.trim().length > 0

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <Field label="Как вас зовут">
        {(props) => (
          <Input
            {...props}
            value={form.name}
            autoComplete="name"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        )}
      </Field>
      <Field label="Телефон" hint="Чтобы связаться, если что-то поменяется">
        {(props) => (
          <Input
            {...props}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        )}
      </Field>
      <Field label="Сколько вас" hint="Считая себя">
        {(props) => (
          <Input
            {...props}
            type="number"
            min={1}
            inputMode="numeric"
            value={form.seats}
            onChange={(e) => setForm({ ...form, seats: e.target.value })}
          />
        )}
      </Field>
      <Field label="Что передать">
        {(props) => (
          <Textarea
            {...props}
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
          />
        )}
      </Field>

      <Checkbox
        checked={consent}
        onChange={() => setConsent(!consent)}
        label={<span className="text-sm text-text-secondary">{excursion.consentWording}</span>}
        labelClassName="items-start"
      />

      {place.isError && (
        <p role="alert" className="text-sm text-accent">
          {errorMessage(place.error)}
        </p>
      )}

      {/* Кнопка называет действие, а не пересказывает шапку: дата и время уже
          стоят над формой, а внутри mono-caps они разгоняли подпись на две
          строки телефона. */}
      <Button type="submit" className="w-full" disabled={!ready || place.isPending}>
        {place.isPending ? 'Записываем…' : 'Записаться'}
      </Button>
    </form>
  )
}
